import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MapPin, Truck, User, Calendar, DollarSign, Navigation, MessageSquare, Plus, Clock, Loader2, FileText, Download, Eye, Upload, Grid3x3, List, Trash2, Pencil } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { saveExtraUploadToDriverFolder } from '../../utils/rateConfirmationStorage';
import { downloadDocument } from '../../utils/downloadDocument';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LoadDetailViewProps {
  loadNumber?: string;
  loadId?: string;
  onBack?: () => void;
}

// Document Card Component
function DocumentCard({ doc, index, onDragStart, onDragOver, onDragLeave, onDrop, isDraggedOver, onDelete }: { 
  doc: any; 
  index: number;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  isDraggedOver: boolean;
  onDelete: (doc: any) => void;
}) {
  const toast = useToast();
  const [fileUrl, setFileUrl] = useState<string>('');
  const [pdfThumbnail, setPdfThumbnail] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Straight to disk: no preview, no new tab. A signed URL on an <a download>
  // would just open the file, because the download attribute is ignored
  // cross-origin.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDocument(doc.file_path, doc.name);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloading(false);
    }
  };
  
  useEffect(() => {
    const getSignedUrl = async () => {
      const { data } = await supabase.storage
        .from('company-documents')
        .createSignedUrl(doc.file_path, 3600); // 1 hour
      if (data?.signedUrl) {
        setFileUrl(data.signedUrl);
        
        // Generate PDF thumbnail if it's a PDF
        if (doc.mime_type === 'application/pdf') {
          generatePdfThumbnail(data.signedUrl);
        }
      }
    };
    getSignedUrl();
  }, [doc.file_path]);
  
  const generatePdfThumbnail = async (url: string) => {
    try {
      console.log('[PDF Thumbnail] Starting generation for:', doc.name);
      
      // Dynamically import pdfjs-dist
      const pdfjsLib = await import('pdfjs-dist');
      
      // Set worker source - use version from node_modules
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs`;
      
      console.log('[PDF Thumbnail] Loading PDF document...');
      const loadingTask = pdfjsLib.getDocument({
        url: url,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/cmaps/',
        cMapPacked: true,
      });
      
      const pdf = await loadingTask.promise;
      console.log('[PDF Thumbnail] PDF loaded, getting first page...');
      
      const page = await pdf.getPage(1);
      
      const scale = 0.5; // Smaller scale for thumbnail
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      if (context) {
        console.log('[PDF Thumbnail] Rendering page...');
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        await page.render(renderContext as any).promise;
        
        const thumbnailData = canvas.toDataURL('image/jpeg', 0.8);
        setPdfThumbnail(thumbnailData);
        console.log('[PDF Thumbnail] Thumbnail generated successfully!');
      }
    } catch (error) {
      console.error('[PDF Thumbnail] Error generating thumbnail:', error);
      console.error('[PDF Thumbnail] Error details:', error instanceof Error ? error.message : 'Unknown error');
    }
  };
  
  const isImage = doc.mime_type?.startsWith('image/');
  const isPDF = doc.mime_type === 'application/pdf';
  
  return (
    <div 
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      className={`border rounded-lg overflow-hidden hover:shadow-md transition-all cursor-move ${
        isDraggedOver 
          ? 'border-blue-500 border-2 bg-blue-50 scale-105' 
          : 'border-slate-200 hover:border-blue-300'
      }`}
    >
      {/* Thumbnail Preview */}
      {isImage ? (
        <div className="relative w-full h-40 bg-slate-100">
          {fileUrl ? (
            <img 
              src={fileUrl} 
              alt={doc.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-16 h-16 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                }
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          )}
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            {doc.mime_type?.split('/')[1]?.toUpperCase()}
          </div>
        </div>
      ) : isPDF ? (
        <div className="relative w-full h-40 bg-red-50 flex items-center justify-center">
          {pdfThumbnail ? (
            <img 
              src={pdfThumbnail} 
              alt={doc.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <FileText className="w-16 h-16 text-red-600" />
          )}
          <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold">
            PDF
          </div>
        </div>
      ) : (
        <div className="relative w-full h-40 bg-slate-100 flex items-center justify-center">
          <FileText className="w-16 h-16 text-slate-400" />
          <div className="absolute top-2 right-2 bg-slate-600 text-white text-xs px-2 py-1 rounded">
            {doc.file_type?.split('/')[1]?.toUpperCase() || 'FILE'}
          </div>
        </div>
      )}
      
      {/* Document Info */}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
          <span className="text-xs text-slate-500">Drag to reorder</span>
        </div>
        <p className="font-medium text-sm text-slate-900 truncate" title={doc.name}>
          {doc.name}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {(doc.file_size / 1024).toFixed(2)} KB
        </p>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setShowPreview(true)}
            disabled={!fileUrl}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
          >
            <Eye className="w-3 h-3" />
            View
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs border border-blue-600 text-blue-600 rounded hover:bg-blue-50 font-medium transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Download className="w-3 h-3" />
                Download
              </>
            )}
          </button>
          <button
            onClick={() => onDelete(doc)}
            className="flex items-center justify-center gap-1 px-3 py-1.5 text-xs border border-red-600 text-red-600 rounded hover:bg-red-50 font-medium transition-colors"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      
      {/* Document Preview Modal */}
      {showPreview && fileUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <div className="relative bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">{doc.name}</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto max-h-[calc(90vh-60px)]">
              {isImage ? (
                <img src={fileUrl} alt={doc.name} className="w-full" />
              ) : isPDF ? (
                <iframe
                  src={fileUrl}
                  className="w-full h-[calc(90vh-60px)]"
                  title={doc.name}
                />
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">Preview not available for this file type</p>
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface Note {
  id: string;
  note: string;
  created_by: string;
  created_at: string;
  author_name?: string;
}

// Load-level data every file of an upload batch is filed under
interface UploadContext {
  loadId: string;
  driverName: string;
  pickupDate: Date;
}

// Document List Item Component for list view
function DocumentListItem({ doc, index, onDragStart, onDragOver, onDragLeave, onDrop, isDraggedOver, onDelete }: { 
  doc: any;
  index: number;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  isDraggedOver: boolean;
  onDelete: (doc: any) => void;
}) {
  const toast = useToast();
  const [fileUrl, setFileUrl] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  
  useEffect(() => {
    const getSignedUrl = async () => {
      const { data } = await supabase.storage
        .from('company-documents')
        .createSignedUrl(doc.file_path, 3600);
      if (data?.signedUrl) {
        setFileUrl(data.signedUrl);
      }
    };
    getSignedUrl();
  }, [doc.file_path]);

  // Saves the file without opening it first (see DocumentCard above).
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDocument(doc.file_path, doc.name);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloading(false);
    }
  };

  const getFileIcon = () => {
    if (doc.mime_type?.includes('pdf')) return '📄';
    if (doc.mime_type?.includes('image')) return '🖼️';
    if (doc.mime_type?.includes('word') || doc.mime_type?.includes('document')) return '📝';
    return '📎';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div 
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-move ${
        isDraggedOver 
          ? 'border-blue-500 border-2 bg-blue-100 scale-105' 
          : 'bg-slate-50 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
      }`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <svg className="w-5 h-5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
        <div className="text-2xl">{getFileIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900 truncate">{doc.name}</p>
          <p className="text-xs text-slate-500">{formatFileSize(doc.size || 0)}</p>
        </div>
      </div>
      <div className="flex gap-2">
        {fileUrl && (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium transition-colors"
          >
            <Eye className="w-3 h-3" />
            View
          </a>
        )}
        {/* Download does not need the signed URL, so it stays outside the guard */}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-blue-600 text-blue-600 rounded hover:bg-blue-50 font-medium transition-colors disabled:opacity-50"
        >
          {downloading ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Download className="w-3 h-3" />
              Download
            </>
          )}
        </button>
        <button
          onClick={() => onDelete(doc)}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border border-red-600 text-red-600 rounded hover:bg-red-50 font-medium transition-colors"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// Roles allowed to send a delivered load to accounting (which also archives it).
const SEND_TO_ACCOUNTING_ROLES = new Set([
  'accounting',
  'accounting manager',
  'admin',
  'superadmin',
  'dispatch',
  'dispatch manager',
]);

// Statuses from which a load may be (re-)sent to accounting.
// 'archived' has to be accepted because it is the status this very action writes
// (handleSendToAccounting sets status: 'ARCHIVED'), and because the billing queue
// itself accepts it: ReadyForBillingView.loadReadyLoads filters on
// .in('status', ['delivered', 'DELIVERED', 'archived', 'ARCHIVED']). Gating on
// 'delivered' alone disabled the button the moment a load was sent, so attaching a
// BOL afterwards meant going back to the dispatch board and re-marking the load.
// 'booked', 'dispatched' and 'removed' are still rejected.
const SEND_TO_ACCOUNTING_STATUSES = new Set(['delivered', 'archived']);

const canSendToAccounting = (status?: string | null) =>
  SEND_TO_ACCOUNTING_STATUSES.has((status || '').trim().toLowerCase());

export function LoadDetailView({ loadNumber, loadId, onBack }: LoadDetailViewProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loadData, setLoadData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; label: string } | null>(null);
  const [documentsViewMode, setDocumentsViewMode] = useState<'grid' | 'list'>('grid');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [draggedDocIndex, setDraggedDocIndex] = useState<number | null>(null);
  const [dragOverDocIndex, setDragOverDocIndex] = useState<number | null>(null);
  const [pickupCoords, setPickupCoords] = useState<[number, number] | null>(null);
  const [deliveryCoords, setDeliveryCoords] = useState<[number, number] | null>(null);
  const [routeCoordinates, setRouteCoordinates] = useState<[number, number][]>([]);
  const [allStops, setAllStops] = useState<any[]>([]);
  const [actualLoadNumber, setActualLoadNumber] = useState(loadNumber || '');
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState('');

  // Update URL when component loads with loadId
  useEffect(() => {
    if (loadId) {
      window.history.replaceState({}, '', `/loads/${loadId}`);
    }
  }, [loadId]);

  // Fetch load by ID if loadId is provided
  useEffect(() => {
    if (loadId && !loadNumber) {
      const fetchLoadNumber = async () => {
        const { data, error } = await supabase
          .from('loads')
          .select('load_number')
          .eq('id', loadId)
          .single();
        
        if (data && !error) {
          setActualLoadNumber(data.load_number);
        }
      };
      fetchLoadNumber();
    }
  }, [loadId, loadNumber]);

  useEffect(() => {
    if (actualLoadNumber) {
      fetchLoadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualLoadNumber]);

  // Fetch documents after loadData is set
  useEffect(() => {
    if (loadData?.id) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData?.id]);

  const fetchLoadData = async () => {
    try {
      setLoading(true);
      
      // Fetch load with stops and dispatch info
      const { data: load, error: loadError } = await supabase
        .from('loads')
        .select(`
          *,
          customers (
            name
          ),
          load_stops!load_stops_load_id_fkey (
            stop_type,
            facility_name,
            street,
            city,
            state,
            zip_code,
            date,
            time_from,
            time_to,
            contact_person,
            phone_number,
            instructions,
            stop_order,
            weight,
            pieces
          ),
          dispatches!dispatches_load_id_fkey (
            dispatch_number,
            status,
            driver_id,
            truck_id,
            drivers!dispatches_driver_id_fkey (
              first_name,
              last_name
            ),
            trucks!dispatches_truck_id_fkey (
              unit_number
            )
          )
        `)
        .eq('load_number', actualLoadNumber)
        .single();

      if (loadError) throw loadError;

      // Sort and store all stops by stop_order
      const sortedStops = load.load_stops ? [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order) : [];
      setAllStops(sortedStops);

      // Find shipper and consignee stops
      const shipperStop = load.load_stops?.find((s: any) => s.stop_type === 'shipper');
      const consigneeStop = load.load_stops?.find((s: any) => s.stop_type === 'consignee');
      const dispatch = load.dispatches?.[0];

      setLoadData({
        id: load.id,
        loadNumber: load.load_number,
        status: load.status || 'Booked',
        isTonu: !!load.is_tonu,
        customer: load.customers?.name || 'N/A',
        pickupLocation: {
          address: shipperStop?.street || '',
          city: shipperStop?.city || load.pickup_city || '',
          state: shipperStop?.state || load.pickup_state || '',
          zipCode: shipperStop?.zip_code || '',
          facilityName: shipperStop?.facility_name || '',
        },
        deliveryLocation: {
          address: consigneeStop?.street || '',
          city: consigneeStop?.city || load.delivery_city || '',
          state: consigneeStop?.state || load.delivery_state || '',
          zipCode: consigneeStop?.zip_code || '',
          facilityName: consigneeStop?.facility_name || '',
        },
        pickupTime: shipperStop?.date && shipperStop?.time_from
          ? (() => {
              const [year, month, day] = shipperStop.date.split('T')[0].split('-');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const [hours, minutes] = shipperStop.time_from.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const hour12 = hour % 12 || 12;
              return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year} at ${hour12}:${minutes} ${ampm}`;
            })()
          : load.pickup_date 
            ? (() => {
                const date = new Date(load.pickup_date);
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth();
                const day = date.getUTCDate();
                const hours = date.getUTCHours();
                const minutes = date.getUTCMinutes();
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const hour12 = hours % 12 || 12;
                return `${monthNames[month]} ${day}, ${year} at ${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
              })()
            : 'Not scheduled',
        deliveryTime: consigneeStop?.date && consigneeStop?.time_from
          ? (() => {
              const [year, month, day] = consigneeStop.date.split('T')[0].split('-');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const [hours, minutes] = consigneeStop.time_from.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const hour12 = hour % 12 || 12;
              return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year} at ${hour12}:${minutes} ${ampm}`;
            })()
          : load.delivery_date 
            ? (() => {
                const date = new Date(load.delivery_date);
                const year = date.getUTCFullYear();
                const month = date.getUTCMonth();
                const day = date.getUTCDate();
                const hours = date.getUTCHours();
                const minutes = date.getUTCMinutes();
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const hour12 = hours % 12 || 12;
                return `${monthNames[month]} ${day}, ${year} at ${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
              })()
            : 'Not scheduled',
        rescheduledDeliveryDate: load.rescheduled_delivery_date || '',
        rescheduledDeliveryTimeFrom: load.rescheduled_delivery_time_from || '',
        rescheduledDeliveryTimeTo: load.rescheduled_delivery_time_to || '',
        distance: load.total_miles || 0,
        rate: load.total_rate || 0,
        weight: load.weight || 0,
        commodity: load.commodity || 'N/A',
        driver: dispatch?.drivers ? `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}` : 'Not assigned',
        truck: dispatch?.trucks?.unit_number || 'Not assigned',
        dispatchNumber: dispatch?.dispatch_number || 'Not dispatched',
        specialInstructions: load.special_instructions || '',
        // The Accounting panel reads these two to decide between the "Ready for
        // Billing" badge and the send button. The select above is `*`, so the row
        // always carries them; they were simply never copied out of it, which left
        // ready_for_billing permanently undefined and the badge unreachable.
        ready_for_billing: !!load.ready_for_billing,
        billing_sent_at: load.billing_sent_at || null,
        invoice_sent: !!load.invoice_sent,
        invoice_number: load.invoice_number || null,
      });

      // Show page immediately - don't wait for map/documents
      setLoading(false);

      // Fetch notes immediately
      loadNotes(load.id);

      // Geocode locations in parallel (non-blocking)
      // Use load stops if available, otherwise fall back to load city/state
      const pickupCity = shipperStop?.city || load.pickup_city;
      const pickupState = shipperStop?.state || load.pickup_state;
      const deliveryCity = consigneeStop?.city || load.delivery_city;
      const deliveryState = consigneeStop?.state || load.delivery_state;

      if (pickupCity && pickupState && deliveryCity && deliveryState) {
        Promise.all([
          geocodeLocation(pickupCity, pickupState),
          geocodeLocation(deliveryCity, deliveryState)
        ]).then(([pickupCoords, deliveryCoords]) => {
          if (pickupCoords) setPickupCoords(pickupCoords);
          if (deliveryCoords) setDeliveryCoords(deliveryCoords);
          
          // Fetch driving route AFTER map is displayed (deferred)
          if (pickupCoords && deliveryCoords) {
            // Delay route calculation to let map render first
            setTimeout(() => {
              fetchDrivingRoute(pickupCoords, deliveryCoords);
            }, 500);
          }
        }).catch(err => console.error('Geocoding error:', err));
      } else if (pickupCity && pickupState) {
        geocodeLocation(pickupCity, pickupState)
          .then(coords => coords && setPickupCoords(coords))
          .catch(err => console.error('Geocoding error:', err));
      } else if (deliveryCity && deliveryState) {
        geocodeLocation(deliveryCity, deliveryState)
          .then(coords => coords && setDeliveryCoords(coords))
          .catch(err => console.error('Geocoding error:', err));
      }
    } catch (error) {
      console.error('Error fetching load:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocuments(true);
      
      if (!loadData?.id) {
        console.log('⚠️ No loadData.id available for fetching documents');
        setDocuments([]);
        setLoadingDocuments(false);
        return;
      }

      console.log('📄 Fetching documents for load_id:', loadData.id);
      
      // Fetch documents from load_documents table with joined documents data
      const { data, error } = await supabase
        .from('load_documents')
        .select(`
          id,
          document_type,
          created_at,
          documents (
            id,
            name,
            file_path,
            file_size,
            file_type,
            created_at
          )
        `)
        .eq('load_id', loadData.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('📄 Raw documents fetched:', data);
      
      // Transform to match expected document format
      const transformedDocs = (data || []).map((doc: any) => {
        // Use joined documents data if available, otherwise fall back to direct fields
        const docData = doc.documents || {};
        const filePath = docData.file_path || doc.file_url || '';
        
        // Determine mime type - check documents.mime_type first, then file extension
        let mimeType = docData.mime_type || 'application/octet-stream';
        if (!docData.mime_type && filePath) {
          if (filePath.toLowerCase().endsWith('.pdf')) {
            mimeType = 'application/pdf';
          } else if (filePath.match(/\.(jpg|jpeg)$/i)) {
            mimeType = 'image/jpeg';
          } else if (filePath.toLowerCase().endsWith('.png')) {
            mimeType = 'image/png';
          }
        }
        
        return {
          id: docData.id || doc.id,
          load_document_id: doc.id,
          name: docData.name || 'Document',
          file_path: filePath,
          file_size: docData.file_size || 0,
          file_type: docData.file_type || doc.document_type,
          mime_type: mimeType,
          created_at: doc.created_at || docData.created_at,
          display_order: doc.display_order,
        };
      });
      
      console.log('📄 Transformed documents:', transformedDocs);
      console.log('📄 First document mime_type:', transformedDocs[0]?.mime_type);
      console.log('📄 First document file_path:', transformedDocs[0]?.file_path);
      setDocuments(transformedDocs);
    } catch (error) {
      console.error('❌ Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const geocodeLocation = async (city: string, state: string): Promise<[number, number] | null> => {
    try {
      const query = `${city}, ${state}`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        {
          headers: {
            'User-Agent': 'TMS-Load-App'
          }
        }
      );
      
      const data = await response.json();
      if (data && data.length > 0) {
        return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const fetchDrivingRoute = async (start: [number, number], end: [number, number]) => {
    try {
      // Use OSRM (Open Source Routing Machine) for truck routing
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=geojson`
      );
      
      const data = await response.json();
      
      if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        // Convert GeoJSON coordinates [lng, lat] to Leaflet format [lat, lng]
        const coordinates: [number, number][] = route.geometry.coordinates.map(
          (coord: number[]) => [coord[1], coord[0]] as [number, number]
        );
        setRouteCoordinates(coordinates);
        console.log(`Route calculated: ${(route.distance / 1609.34).toFixed(0)} miles, ${(route.duration / 3600).toFixed(1)} hours`);
      } else {
        // Expected fallback when OSRM is unavailable — draw a straight line.
        console.log('Route unavailable, using straight line. OSRM code:', data?.code);
        setRouteCoordinates([start, end]);
      }
    } catch (error) {
      // Expected fallback when OSRM is unavailable — draw a straight line.
      console.log('Route unavailable, using straight line:', error);
      setRouteCoordinates([start, end]);
    }
  };

  const loadNotes = async (loadIdToUse: string) => {
    try {
      const { data, error } = await supabase
        .from('load_notes')
        .select(`
          id,
          note,
          created_by,
          created_at,
          profiles:created_by(full_name)
        `)
        .eq('load_id', loadIdToUse)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedNotes = (data || []).map((note: any) => ({
        id: note.id,
        note: note.note,
        created_by: note.created_by,
        created_at: note.created_at,
        author_name: note.profiles?.full_name || 'Unknown User'
      }));

      setNotes(formattedNotes);
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !loadData?.id || !profile?.id) return;

    try {
      const { data, error } = await supabase
        .from('load_notes')
        .insert({
          load_id: loadData.id,
          note: newNote,
          created_by: profile.id
        })
        .select(`
          id,
          note,
          created_by,
          created_at,
          profiles:created_by(full_name)
        `)
        .single();

      if (error) throw error;

      const formattedNote = {
        id: data.id,
        note: data.note,
        created_by: data.created_by,
        created_at: data.created_at,
        author_name: (data.profiles as any)?.full_name || profile.full_name || 'Unknown User'
      };

      setNotes([formattedNote, ...notes]);
      setNewNote('');
      toast.success('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    // Check if we're dragging a document for reordering (not a file from outside)
    if (draggedDocIndex !== null) {
      // This is a document reorder operation, not a file upload
      return;
    }

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0 || !loadData) return;

    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validFiles = files.filter(f => allowedTypes.includes(f.type));
    const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type: ${invalidFiles.map(f => f.name).join(', ')}. Please upload PDF, JPG, PNG, DOC, or DOCX files.`);
    }
    if (validFiles.length === 0) return;

    await processFileBatch(validFiles);
  };

  const handleDocDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedDocIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDocDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDocIndex(index);
  };

  const handleDocDragLeave = () => {
    setDragOverDocIndex(null);
  };

  const handleDocDrop = async (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedDocIndex === null || draggedDocIndex === dropIndex) {
      setDraggedDocIndex(null);
      setDragOverDocIndex(null);
      setIsDraggingOver(false);
      return;
    }

    // Reorder documents array
    const newDocuments = [...documents];
    const [draggedDoc] = newDocuments.splice(draggedDocIndex, 1);
    newDocuments.splice(dropIndex, 0, draggedDoc);
    
    // Update local state immediately for smooth UX
    setDocuments(newDocuments);
    setDraggedDocIndex(null);
    setDragOverDocIndex(null);
    setIsDraggingOver(false);

    // Update display_order in database
    try {
      const updates = newDocuments.map((doc, index) => ({
        id: doc.load_document_id,
        display_order: index + 1
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('load_documents')
          .update({ display_order: update.display_order })
          .eq('id', update.id);
        
        if (error) throw error;
      }

      toast.success('Document order updated');
    } catch (error) {
      console.error('Error updating document order:', error);
      toast.error('Failed to update document order');
      // Revert to original order on error
      fetchDocuments();
    }
  };

  const handleDeleteDocument = async (doc: any) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.name}"?`)) {
      return;
    }

    try {
      // Delete from load_documents table
      const { error: deleteError } = await supabase
        .from('load_documents')
        .delete()
        .eq('id', doc.load_document_id);

      if (deleteError) throw deleteError;

      // Delete file from storage
      if (doc.file_path) {
        const { error: storageError } = await supabase.storage
          .from('company-documents')
          .remove([doc.file_path]);

        if (storageError) {
          console.error('Error deleting file from storage:', storageError);
          // Don't throw error here as the database record is already deleted
        }
      }

      toast.success('Document deleted successfully');
      
      // Refresh documents list
      await fetchDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error(error.message || 'Failed to delete document');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !loadData) return;

    await processFileBatch(files);
  };

  // Resolves the driver folder and pickup date the uploads get filed under.
  // It is the same for every file of a batch, so it runs once per batch instead
  // of once per file.
  const resolveUploadContext = async (loadId: string): Promise<UploadContext> => {
    // Get load details including driver info and pickup date
    const { data: loadDetails, error: loadError } = await supabase
      .from('loads')
      .select(`
        *,
        load_stops!load_stops_load_id_fkey (date, stop_type),
        dispatches!dispatches_load_id_fkey (
          driver_id,
          created_at,
          drivers!dispatches_driver_id_fkey (first_name, last_name)
        )
      `)
      .eq('id', loadId)
      .single();

    if (loadError) throw loadError;

    // Get the most recent dispatch (last one created)
    const sortedDispatches = loadDetails.dispatches?.sort((a: any, b: any) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const dispatch = sortedDispatches?.[0];
    
    console.log('📋 All dispatches for load:', loadDetails.dispatches);
    console.log('📋 Using most recent dispatch:', dispatch);
    
    const driverName = dispatch?.drivers 
      ? `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}` 
      : null;
    
    console.log('📋 Driver name for file upload:', driverName);
    
    const shipperStop = loadDetails.load_stops?.find((s: any) => s.stop_type === 'shipper');
    const pickupDate = shipperStop?.date 
      ? new Date(shipperStop.date) 
      : loadDetails.pickup_date 
        ? new Date(loadDetails.pickup_date) 
        : new Date();

    if (!driverName) {
      throw new Error('Cannot upload document: Load must have an assigned driver');
    }

    return { loadId, driverName, pickupDate };
  };

  // Files are uploaded one at a time on purpose: saveExtraUploadToDriverFolder
  // creates the document_folders rows on demand and resolves name collisions by
  // listing the folder, so parallel calls would race on both.
  const processFileBatch = async (files: File[]) => {
    if (!loadData || files.length === 0) return;

    setUploadingFile(true);
    setUploadProgress({ current: 0, total: files.length, label: '' });

    try {
      const context = await resolveUploadContext(loadData.id);

      // A file that fails must not abort the rest of the batch
      const failed: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length, label: file.name });
        try {
          await processFileUpload(file, context);
        } catch (error) {
          console.error('Error uploading file:', file.name, error);
          failed.push(`${file.name} (${(error as { message?: string })?.message || 'upload failed'})`);
        }
      }

      const uploaded = files.length - failed.length;
      if (uploaded > 0) {
        toast.success(uploaded === 1 ? 'Document uploaded successfully' : `${uploaded} documents uploaded successfully`);
      }
      if (failed.length > 0) {
        toast.error(`Failed to upload ${failed.length} of ${files.length}: ${failed.join(', ')}`);
      }

      // Refresh documents list once for the whole batch
      await fetchDocuments();
    } catch (error: any) {
      console.error('Error uploading files:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploadingFile(false);
      setUploadProgress(null);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const processFileUpload = async (file: File, context: UploadContext) => {
    // First, upload file to temporary location
    const timestamp = Date.now();
    // Sanitize filename (remove spaces and special characters)
    const sanitizedFileName = file.name
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^\w\-_.]/g, '');  // Remove special characters except dash, underscore, dot
    // Random suffix so two same-named files in one batch can't collide here
    const tempFilePath = `temp/${timestamp}-${Math.random().toString(36).substring(2, 8)}-${sanitizedFileName}`;
    
    const { error: uploadError } = await supabase.storage
      .from('company-documents')
      .upload(tempFilePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    // Save to driver folder structure in 'Extra Uploads' subfolder
    const result = await saveExtraUploadToDriverFolder({
      originalFilePath: tempFilePath,
      originalFileName: file.name,
      driverName: context.driverName,
      pickupDate: context.pickupDate,
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to save document to driver folder');
    }

    // Link document to this load via load_documents
    if (result.documentId) {
      const { error: linkError } = await supabase
        .from('load_documents')
        .insert({
          load_id: context.loadId,
          document_id: result.documentId,
          document_type: 'extra_upload',
        });

      if (linkError) {
        console.error('Failed to link document to load:', linkError);
      }
    }

    // Delete temporary file
    await supabase.storage
      .from('company-documents')
      .remove([tempFilePath]);
  };

  const handleSendToAccounting = async () => {
    if (!loadData) return;

    try {
      const { data: updatedLoads, error } = await supabase
        .from('loads')
        .update({
          ready_for_billing: true,
          billing_sent_at: new Date().toISOString(),
          status: 'ARCHIVED', // Automatically archive when sent to accounting
        })
        .eq('id', loadData.id)
        .select('id');

      if (error) throw error;

      // An RLS policy that excludes this user simply matches zero rows: PostgREST
      // returns success with an empty result set, so without this check we would
      // show a green toast for a write that never happened.
      if (!updatedLoads || updatedLoads.length === 0) {
        throw new Error(
          'Load could not be updated - your role may not have permission to send loads to accounting. Ask a dispatcher or admin to do it.'
        );
      }

      // Also archive the associated dispatch
      const { error: dispatchError } = await supabase
        .from('dispatches')
        .update({
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('load_id', loadData.id);

      if (dispatchError) {
        console.error('Error archiving dispatch:', dispatchError);
        // Don't throw - load was archived successfully
      }

      toast.success('Load sent to accounting and archived successfully!');
      
      // Reload load data to show updated status
      fetchLoadData();
    } catch (error: any) {
      console.error('Error sending to accounting:', error);
      toast.error(error.message || 'Failed to send to accounting');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading load details...</p>
        </div>
      </div>
    );
  }

  if (!loadData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-slate-600">Load not found</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Loads
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Map on Right */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              if (onBack) {
                onBack();
              } else if (window.history.length > 1) {
                window.history.back();
              } else {
                window.history.pushState({}, '', '/loads');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            }}
            className="flex items-center gap-2 text-[#AC021A] hover:text-[#8A0115] font-semibold"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Loads
          </button>
          <button
            onClick={() => navigate(`/loads/edit/${loadData.id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-[#AC021A] text-white rounded-lg hover:bg-[#8A0115] font-semibold text-sm"
          >
            <Pencil className="w-4 h-4" />
            Edit Load
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Load Info */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3 flex items-center gap-3 flex-wrap">
              Load {actualLoadNumber || loadNumber}
              {loadData.isTonu && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 align-middle">
                  TONU
                </span>
              )}
            </h1>
            <div className="flex items-center gap-3 mb-6">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                {loadData.status}
              </span>
              {loadData.dispatchNumber && (
                <span className="text-sm text-slate-600">
                  Dispatch: <span className="font-semibold text-[#AC021A]">{loadData.dispatchNumber}</span>
                </span>
              )}
            </div>
            
            {/* Rate Info */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <p className="text-xs text-slate-600 mb-1">Total Rate</p>
                <p className="text-2xl font-bold text-[#AC021A]">${loadData.rate.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-600 mb-1">Miles</p>
                <p className="text-2xl font-bold text-slate-900">{loadData.distance.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-600 mb-1">Rate/Mile</p>
                <p className="text-2xl font-bold text-green-600">
                  ${loadData.distance > 0 ? (loadData.rate / loadData.distance).toFixed(2) : '0.00'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Compact Map */}
          <div className="h-64 bg-slate-100 rounded-lg overflow-hidden border border-slate-200 relative">
            {pickupCoords && deliveryCoords ? (
              <MapContainer
                bounds={[pickupCoords, deliveryCoords]}
                style={{ height: '100%', width: '100%' }}
                boundsOptions={{ padding: [30, 30] }}
                attributionControl={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Pickup Marker */}
                <Marker
                  position={pickupCoords}
                  icon={L.divIcon({
                    className: 'custom-location-marker',
                    html: `
                      <div style="
                        background: #10B981;
                        color: white;
                        padding: 6px 10px;
                        border-radius: 6px;
                        font-weight: bold;
                        font-size: 12px;
                        box-shadow: 0 3px 10px rgba(16, 185, 129, 0.5);
                        white-space: nowrap;
                        border: 2px solid white;
                      ">
                        📦 Pickup
                      </div>
                    `,
                    iconSize: [80, 32],
                    iconAnchor: [40, 32],
                  })}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold text-green-600">📦 Pickup</div>
                      <div>{loadData.pickupLocation.city}, {loadData.pickupLocation.state}</div>
                    </div>
                  </Popup>
                </Marker>

                {/* Delivery Marker */}
                <Marker
                  position={deliveryCoords}
                  icon={L.divIcon({
                    className: 'custom-location-marker',
                    html: `
                      <div style="
                        background: #EF4444;
                        color: white;
                        padding: 6px 10px;
                        border-radius: 6px;
                        font-weight: bold;
                        font-size: 12px;
                        box-shadow: 0 3px 10px rgba(239, 68, 68, 0.5);
                        white-space: nowrap;
                        border: 2px solid white;
                      ">
                        🎯 Delivery
                      </div>
                    `,
                    iconSize: [90, 32],
                    iconAnchor: [45, 32],
                  })}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold text-red-600">🎯 Delivery</div>
                      <div>{loadData.deliveryLocation.city}, {loadData.deliveryLocation.state}</div>
                    </div>
                  </Popup>
                </Marker>

                {/* Route Line - Shows actual driving route */}
                {routeCoordinates.length > 0 ? (
                  <Polyline
                    positions={routeCoordinates}
                    color="#3B82F6"
                    weight={4}
                    opacity={0.7}
                  />
                ) : (
                  <Polyline
                    positions={[pickupCoords, deliveryCoords]}
                    color="#3B82F6"
                    weight={4}
                    opacity={0.7}
                    dashArray="10, 10"
                  />
                )}
              </MapContainer>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Loading route map...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pickup Details */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-green-100 p-1.5 rounded-lg">
              <MapPin className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm">Pickup Location</h3>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-slate-600">Address</p>
            <p className="font-semibold text-sm text-slate-900">{loadData.pickupLocation.address}</p>
            <p className="text-sm text-slate-700">
              {loadData.pickupLocation.city}, {loadData.pickupLocation.state} {loadData.pickupLocation.zipCode}
            </p>
            <div className="pt-1.5 border-t border-slate-200">
              <p className="text-xs text-slate-600 mb-1">Scheduled Pickup</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <p className="font-semibold text-sm text-slate-900">{loadData.pickupTime}</p>
              </div>
              {loadData.specialInstructions && loadData.specialInstructions.includes('Pickup window:') && (
                <div className="mt-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs font-medium text-orange-800">
                      {loadData.specialInstructions.split('|').find((s: string) => s.includes('Pickup window:'))?.trim().replace('Pickup window:', 'Window:')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delivery Details */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-blue-100 p-1.5 rounded-lg">
              <Navigation className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm">Delivery Location</h3>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-slate-600">Address</p>
            <p className="font-semibold text-sm text-slate-900">{loadData.deliveryLocation.address}</p>
            <p className="text-sm text-slate-700">
              {loadData.deliveryLocation.city}, {loadData.deliveryLocation.state} {loadData.deliveryLocation.zipCode}
            </p>
            <div className="pt-1.5 border-t border-slate-200">
              <p className="text-xs text-slate-600 mb-1">Scheduled Delivery</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <p className="font-semibold text-sm text-slate-900">{loadData.deliveryTime}</p>
              </div>
              {loadData.specialInstructions && loadData.specialInstructions.includes('Delivery window:') && (
                <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs font-medium text-blue-800">
                      {loadData.specialInstructions.split('|').find((s: string) => s.includes('Delivery window:'))?.trim().replace('Delivery window:', 'Window:')}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Rescheduled Delivery Info */}
              {(loadData.rescheduledDeliveryDate || loadData.rescheduledDeliveryTimeFrom) && (
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <p className="text-xs text-orange-600 mb-1 font-medium">⚠️ Rescheduled Delivery</p>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-orange-500" />
                    <p className="font-semibold text-sm text-orange-900">
                      {loadData.rescheduledDeliveryDate && (() => {
                        const dateStr = loadData.rescheduledDeliveryDate;
                        const timeStr = loadData.rescheduledDeliveryTimeFrom || '00:00:00';
                        const combinedDateTime = `${dateStr}T${timeStr}`;
                        return new Date(combinedDateTime).toLocaleString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });
                      })()}
                      {loadData.rescheduledDeliveryTimeFrom && loadData.rescheduledDeliveryTimeTo && 
                        ` (Window: ${loadData.rescheduledDeliveryTimeFrom}-${loadData.rescheduledDeliveryTimeTo})`
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* All Stops Section - Show if there are any stops */}
      {allStops.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            {allStops.length > 2 ? `All Stops (${allStops.length})` : 'Stop Details'}
          </h3>
          <div className="space-y-3">
            {allStops.map((stop: any, index: number) => {
              // Calculate display number: PICKUP is always #1, deliveries are numbered sequentially
              const deliveryStops = allStops.filter(s => s.stop_type === 'consignee');
              const deliveryIndex = deliveryStops.findIndex(s => s.stop_order === stop.stop_order);
              const displayNumber = stop.stop_type === 'shipper' ? 1 : deliveryIndex + 1;
              
              return (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    stop.stop_type === 'shipper' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          stop.stop_type === 'shipper' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-blue-600 text-white'
                        }`}>
                          {stop.stop_type === 'shipper' ? '📦 PICKUP' : '🎯 DELIVERY'} #{displayNumber}
                        </span>
                        {stop.facility_name && (
                          <span className="font-semibold text-slate-900">{stop.facility_name}</span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-600 mb-1">Address</p>
                        <p className="font-medium text-slate-900">
                          {stop.street && `${stop.street}, `}
                          {stop.city}, {stop.state} {stop.zip_code}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-slate-600 mb-1">Date & Time</p>
                        <p className="font-medium text-slate-900">
                          {(() => {
                            const [year, month, day] = stop.date.split('T')[0].split('-');
                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
                          })()}
                          {stop.time_from && ` at ${stop.time_from}`}
                          {stop.time_to && ` - ${stop.time_to}`}
                        </p>
                      </div>
                      
                      <div>
                        {stop.contact_person && (
                          <div className="mb-2">
                            <p className="text-xs text-slate-600">Contact</p>
                            <p className="font-medium text-slate-900">{stop.contact_person}</p>
                          </div>
                        )}
                        {stop.phone_number && (
                          <div>
                            <p className="text-xs text-slate-600">Phone</p>
                            <p className="font-medium text-slate-900">{stop.phone_number}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {(stop.weight || stop.pieces) && (
                      <div className="mt-3 pt-3 border-t border-slate-200 flex gap-4">
                        {stop.weight && (
                          <div>
                            <p className="text-xs text-slate-600">Weight</p>
                            <p className="font-semibold text-slate-900">{stop.weight.toLocaleString()} lbs</p>
                          </div>
                        )}
                        {stop.pieces && (
                          <div>
                            <p className="text-xs text-slate-600">Pieces</p>
                            <p className="font-semibold text-slate-900">{stop.pieces}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {stop.instructions && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-xs text-slate-600 mb-1">Instructions</p>
                        <p className="text-sm text-slate-700">{stop.instructions}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Load Details */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-900 mb-3 text-sm">Load Details</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Customer</p>
            <p className="font-semibold text-sm text-slate-900">{loadData.customer}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Driver</p>
            <div className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-slate-500" />
              <p className="font-semibold text-sm text-slate-900">{loadData.driver}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Truck</p>
            <div className="flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-slate-500" />
              <p className="font-semibold text-sm text-slate-900">{loadData.truck}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Distance</p>
            <p className="font-semibold text-sm text-slate-900">{loadData.distance} mi</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Weight</p>
            <p className="font-semibold text-sm text-slate-900">{loadData.weight.toLocaleString()} lbs</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Commodity</p>
            <p className="font-semibold text-sm text-slate-900">{loadData.commodity}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-0.5">Rate</p>
            <div className="flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-slate-500" />
              <p className="font-semibold text-sm text-slate-900">${loadData.rate.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Special Instructions Section */}
      {loadData.specialInstructions && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-3 text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-600" />
            Special Instructions
          </h3>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {loadData.specialInstructions.split('|').map((instruction: string, index: number) => (
                <span key={index}>
                  {instruction.trim()}
                  {index < loadData.specialInstructions.split('|').length - 1 && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                </span>
              ))}
            </p>
          </div>
        </div>
      )}

      {/* Documents Section */}
      <div 
        className="bg-white rounded-xl shadow-sm border border-slate-200"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <h3 className="font-semibold text-slate-900 text-sm">Documents ({documents.length})</h3>
            </div>
            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setDocumentsViewMode('grid')}
                  className={`p-1.5 rounded transition-colors ${
                    documentsViewMode === 'grid'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Grid View"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDocumentsViewMode('list')}
                  className={`p-1.5 rounded transition-colors ${
                    documentsViewMode === 'list'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="List View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              {uploadProgress && uploadProgress.current > 0 && (
                <span className="text-xs text-slate-600 truncate max-w-[220px]">
                  Uploading {uploadProgress.current}/{uploadProgress.total}: {uploadProgress.label}
                </span>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                disabled={uploadingFile}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {uploadingFile ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {uploadProgress && uploadProgress.total > 1
                      ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                      : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5" />
                    Upload File
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className={`p-4 transition-all ${
          isDraggingOver && draggedDocIndex === null
            ? 'bg-blue-50 border-2 border-dashed border-blue-400' 
            : ''
        }`}>
          {isDraggingOver && draggedDocIndex === null ? (
            <div className="text-center py-12">
              <Upload className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-bounce" />
              <p className="text-lg font-semibold text-blue-700">Drop files here to upload</p>
              <p className="text-sm text-blue-600 mt-2">PDF, JPG, PNG, DOC, or DOCX files</p>
            </div>
          ) : loadingDocuments ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
              <p className="text-sm text-slate-600">Loading documents...</p>
            </div>
          ) : documents.length > 0 ? (
            documentsViewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {documents.map((doc, index) => (
                  <DocumentCard 
                    key={doc.id} 
                    doc={doc} 
                    index={index}
                    onDragStart={handleDocDragStart}
                    onDragOver={handleDocDragOver}
                    onDragLeave={handleDocDragLeave}
                    onDrop={handleDocDrop}
                    isDraggedOver={dragOverDocIndex === index}
                    onDelete={handleDeleteDocument}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc, index) => (
                  <DocumentListItem 
                    key={doc.id} 
                    doc={doc} 
                    index={index}
                    onDragStart={handleDocDragStart}
                    onDragOver={handleDocDragOver}
                    onDragLeave={handleDocDragLeave}
                    onDrop={handleDocDrop}
                    isDraggedOver={dragOverDocIndex === index}
                    onDelete={handleDeleteDocument}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600">No documents uploaded</p>
              <p className="text-xs text-slate-500 mt-2">Click "Upload File" to add documents</p>
            </div>
          )}
        </div>
      </div>

      {/* Accounting Section - Accounting, Dispatch, Admin and Superadmin.
          Dispatch and Dispatch Manager were added because they are the ones who
          close out a delivered load; without them the load never reaches
          accounting and never gets archived.
          Role is normalised rather than compared against both capitalisations:
          UserRole carries 18 values in two casings and profiles holds both
          (e.g. 'Superadmin' and 'superadmin' both exist in this tenant). */}
      {SEND_TO_ACCOUNTING_ROLES.has((profile?.role || '').trim().toLowerCase()) && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-600" />
              <h3 className="font-semibold text-slate-900 text-sm">Accounting</h3>
            </div>
          </div>
          <div className="p-4">
            {loadData?.ready_for_billing ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-900">Ready for Billing</p>
                      <p className="text-sm text-green-700">This load has been sent to accounting</p>
                    </div>
                  </div>
                  <div className="text-xs text-green-600 font-medium">
                    {loadData.billing_sent_at && new Date(loadData.billing_sent_at).toLocaleDateString()}
                  </div>
                </div>
                {/* Already sent, but uploading a BOL after the hand-off is the normal
                    workflow here, so accounting needs a way to push the load again.
                    handleSendToAccounting rewrites the same three fields, so this is
                    idempotent - only billing_sent_at moves.
                    Once an invoice has been generated this is a dead end, though:
                    ReadyForBillingView.loadReadyLoads filters .eq('invoice_sent', false),
                    so the load cannot return to the queue until accounting returns it
                    from the Invoices tab. Sending again would report success and change
                    nothing the user can see, so say so instead of offering the button. */}
                {loadData?.invoice_sent ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    An invoice{loadData.invoice_number ? ` (#${loadData.invoice_number})` : ''} has already
                    been generated for this load, so it no longer appears under Ready for Billing.
                    To add a document now, return the load from the Invoices tab first.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500">
                      Uploaded a document after sending? Send it again to refresh accounting's copy.
                    </p>
                    <button
                      onClick={handleSendToAccounting}
                      disabled={!canSendToAccounting(loadData?.status)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <DollarSign className="w-4 h-4" />
                      Send again
                    </button>
                    {!canSendToAccounting(loadData?.status) && (
                      <p className="text-xs text-amber-600 text-center">
                        Load must be delivered or archived to be sent to accounting
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Mark this load as ready for billing when all documents are uploaded and the load is completed.
                </p>
                <button
                  onClick={handleSendToAccounting}
                  disabled={!canSendToAccounting(loadData?.status)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DollarSign className="w-5 h-5" />
                  Send to Accounting
                </button>
                {!canSendToAccounting(loadData?.status) && (
                  <p className="text-xs text-amber-600 text-center">
                    Load must be delivered or archived before sending to accounting
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-slate-900 text-sm">Notes ({notes.length})</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <textarea
              placeholder="Add a note about this load..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#AC021A] focus:border-[#AC021A] resize-none text-sm"
              rows={2}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#AC021A] text-white rounded-lg hover:bg-[#8A0115] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Note
              </button>
            </div>
          </div>

          {/* Notes List */}
          <div className="space-y-2.5">
            {notes.map((note) => (
              <div key={note.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full bg-[#AC021A] text-white flex items-center justify-center text-xs font-semibold">
                      {(note.author_name || 'U').split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-slate-900">{note.author_name || 'Unknown User'}</p>
                      <div className="flex items-center gap-0.5 text-[10px] text-slate-500">
                        <Clock className="w-2.5 h-2.5" />
                        {new Date(note.created_at).toLocaleString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 ml-7">{note.note}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
